import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { Person, Document, DocumentType } from '../../../types';
import { useUser } from '../../../contexts/AuthContext';
import { DocumentService } from '../../../services/documentService';
import DocumentList from '../../documents/DocumentList';
import DocumentUpload from '../../documents/DocumentUpload';
import DocumentViewer from '../../documents/DocumentViewer';

interface DocumentsTabProps {
  person: Person;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ person }) => {
  const currentUser = useUser();

  // Document state
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  // Filter state
  const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all');
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'approved' | 'pending'>('approved');

  // Notification state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch documents for the person
  const fetchDocuments = useCallback(async () => {
    if (!person?.id || !currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const fetchedDocuments = await DocumentService.getDocumentsByPerson(person.id);
      setDocuments(fetchedDocuments);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [person?.id, currentUser]);

  // Load documents on component mount and person change
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle document upload completion
  const handleUploadComplete = useCallback(async (uploadedDocument: Document) => {
    // Add new document to the current list
    setDocuments(prev => [uploadedDocument, ...prev]);
    setShowUploadDialog(false);

    // Show appropriate message based on approval status
    const message = uploadedDocument.approvalStatus === 'pending'
      ? 'Document uploaded successfully and is awaiting approval'
      : 'Document uploaded successfully';

    setSnackbar({
      open: true,
      message,
      severity: uploadedDocument.approvalStatus === 'pending' ? 'info' : 'success'
    });
  }, []);

  // Handle document click to open viewer
  const handleDocumentClick = useCallback((document: Document) => {
    setSelectedDocument(document);
    setShowDocumentViewer(true);
  }, []);

  // Handle document update (metadata, transcription, etc.)
  const handleUpdateDocument = useCallback(async (documentId: string, updates: any) => {
    if (!currentUser) {
      throw new Error('You must be logged in to update documents');
    }

    try {
      await DocumentService.updateDocument(
        documentId,
        updates,
        currentUser.uid,
        currentUser.displayName,
        currentUser.role
      );

      // Refetch the updated document
      const updatedDocument = await DocumentService.getDocumentById(documentId);
      if (!updatedDocument) {
        throw new Error('Failed to retrieve updated document');
      }

      // Update the document in state
      setDocuments(prev => prev.map(doc =>
        doc.id === documentId ? updatedDocument : doc
      ));

      setSnackbar({
        open: true,
        message: 'Document updated successfully',
        severity: 'success'
      });

      return updatedDocument;
    } catch (err) {
      console.error('Error updating document:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update document',
        severity: 'error'
      });
      throw err;
    }
  }, [currentUser]);

  // Handle document deletion
  const handleDeleteDocument = useCallback(async (documentId: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to delete documents');
    }

    const confirmMessage = 'Are you sure you want to delete this document? This cannot be undone.';
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      await DocumentService.deleteDocument(
        documentId,
        currentUser.uid,
        currentUser.displayName,
        currentUser.role
      );

      // Remove document from state
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));

      // Close viewer if this document was open
      if (selectedDocument?.id === documentId) {
        setShowDocumentViewer(false);
        setSelectedDocument(null);
      }

      setSnackbar({
        open: true,
        message: 'Document deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting document:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete document',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedDocument]);

  // Handle document approval (Admin only)
  const handleApproveDocument = useCallback(async (documentId: string) => {
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Only administrators can approve documents');
    }

    try {
      await DocumentService.approveDocument(
        documentId,
        currentUser.uid,
        currentUser.displayName
      );

      // Refetch the updated document
      const updatedDocument = await DocumentService.getDocumentById(documentId);
      if (!updatedDocument) {
        throw new Error('Failed to retrieve updated document');
      }

      // Update the document in state
      setDocuments(prev => prev.map(doc =>
        doc.id === documentId ? updatedDocument : doc
      ));

      setSnackbar({
        open: true,
        message: 'Document approved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error approving document:', err);
      setSnackbar({
        open: true,
        message: 'Failed to approve document',
        severity: 'error'
      });
    }
  }, [currentUser]);

  // Handle document rejection (Admin only)
  const handleRejectDocument = useCallback(async (documentId: string, reason: string) => {
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Only administrators can reject documents');
    }

    try {
      await DocumentService.rejectDocument(
        documentId,
        reason,
        currentUser.uid,
        currentUser.displayName
      );

      // Refetch the updated document
      const updatedDocument = await DocumentService.getDocumentById(documentId);
      if (!updatedDocument) {
        throw new Error('Failed to retrieve updated document');
      }

      // Update the document in state
      setDocuments(prev => prev.map(doc =>
        doc.id === documentId ? updatedDocument : doc
      ));

      setSnackbar({
        open: true,
        message: 'Document rejected',
        severity: 'info'
      });
    } catch (err) {
      console.error('Error rejecting document:', err);
      setSnackbar({
        open: true,
        message: 'Failed to reject document',
        severity: 'error'
      });
    }
  }, [currentUser]);

  // Handle upload dialog cancel
  const handleUploadCancel = useCallback(() => {
    setShowUploadDialog(false);
  }, []);

  // Handle document viewer close
  const handleDocumentViewerClose = useCallback(() => {
    setShowDocumentViewer(false);
    setSelectedDocument(null);
  }, []);

  // Handle snackbar close
  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Filter documents based on current filters
  const filteredDocuments = documents.filter(doc => {
    // Type filter
    if (typeFilter !== 'all' && doc.type !== typeFilter) {
      return false;
    }

    // Approval filter
    if (approvalFilter === 'approved' && doc.approvalStatus !== 'approved') {
      return false;
    }
    if (approvalFilter === 'pending' && doc.approvalStatus !== 'pending') {
      return false;
    }

    return true;
  });

  // Count pending documents
  const pendingCount = documents.filter(doc => doc.approvalStatus === 'pending').length;

  // Check if user can upload
  const canUpload = currentUser && ['contributor', 'editor', 'admin'].includes(currentUser.role);

  // Loading state
  if (loading && documents.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error && documents.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchDocuments}>
          Retry
        </Button>
      </Box>
    );
  }

  // Not logged in state
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please log in to view and manage documents.
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (documents.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Documents Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Start building {person.firstName}'s document collection by uploading certificates, letters, and other records.
            </Typography>
            {canUpload && (
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setShowUploadDialog(true)}
                size="large"
              >
                Upload Document
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        {canUpload && (
          <Dialog
            open={showUploadDialog}
            onClose={handleUploadCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: { minHeight: '500px' }
            }}
          >
            <DocumentUpload
              personId={person.id}
              personName={`${person.firstName} ${person.lastName}`}
              currentUser={currentUser}
              onUploadComplete={handleUploadComplete}
              onCancel={handleUploadCancel}
            />
          </Dialog>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Main documents view
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Documents ({filteredDocuments.length})
          </Typography>
          {pendingCount > 0 && currentUser.role === 'admin' && (
            <Chip
              icon={<PendingIcon />}
              label={`${pendingCount} pending`}
              color="warning"
              size="small"
            />
          )}
        </Box>
        {canUpload && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowUploadDialog(true)}
            disabled={loading}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {/* Loading overlay for operations */}
      {loading && documents.length > 0 && (
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            Processing documents...
          </Alert>
        </Box>
      )}

      {/* Document List */}
      <DocumentList
        documents={filteredDocuments}
        loading={loading}
        currentUser={currentUser}
        typeFilter={typeFilter}
        approvalFilter={approvalFilter}
        onTypeFilterChange={setTypeFilter}
        onApprovalFilterChange={setApprovalFilter}
        onDocumentClick={handleDocumentClick}
        onDeleteDocument={handleDeleteDocument}
        onApproveDocument={handleApproveDocument}
        onRejectDocument={handleRejectDocument}
      />

      {/* Upload Dialog */}
      {canUpload && (
        <Dialog
          open={showUploadDialog}
          onClose={handleUploadCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { minHeight: '500px' }
          }}
        >
          <DocumentUpload
            personId={person.id}
            personName={`${person.firstName} ${person.lastName}`}
            currentUser={currentUser}
            onUploadComplete={handleUploadComplete}
            onCancel={handleUploadCancel}
          />
        </Dialog>
      )}

      {/* Document Viewer */}
      {showDocumentViewer && selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          currentUser={currentUser}
          open={showDocumentViewer}
          onClose={handleDocumentViewerClose}
          onUpdateDocument={handleUpdateDocument}
          onDeleteDocument={handleDeleteDocument}
          onApproveDocument={handleApproveDocument}
          onRejectDocument={handleRejectDocument}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentsTab;
